<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import AppDrawer from '@/components/AppDrawer.vue';
import { notify } from '@/services/notice';
import type { Project } from '@/types/domain';
import type { OwnerAssignmentPreview } from '@/types/owner-assignment';
import { errorMessage } from '@/utils/format';

const props = defineProps<{ project: Project }>();
const emit = defineEmits<{ done: [] }>();
const open = ref(false);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const preview = ref<OwnerAssignmentPreview | null>(null);
const choices = ref<Record<number, number | null>>({});
let requestId = 0;
const assignments = computed(() => (preview.value?.rows ?? []).flatMap((row) => {
  const userId = choices.value[row.nodeId];
  return userId ? [{ nodeId: row.nodeId, userId, version: row.version }] : [];
}));

async function refresh(): Promise<void> {
  const request = ++requestId;
  loading.value = true;
  error.value = '';
  preview.value = null;
  choices.value = {};
  try {
    const result = await eqsApi.previewPlanOwners(props.project.id);
    if (request !== requestId) return;
    preview.value = result;
    choices.value = Object.fromEntries(result.rows.map((row) => [row.nodeId, row.suggestedUserId]));
  } catch (caught) {
    if (request === requestId) error.value = errorMessage(caught);
  } finally {
    if (request === requestId) loading.value = false;
  }
}

function close(): void {
  if (saving.value) return;
  requestId += 1;
  open.value = false;
  preview.value = null;
}
watch(() => props.project.id, close);

async function show(): Promise<void> {
  open.value = true;
  await refresh();
}
async function confirm(): Promise<void> {
  if (saving.value || loading.value || !assignments.value.length) return;
  saving.value = true;
  error.value = '';
  try {
    const result = await eqsApi.confirmPlanOwners(props.project.id, assignments.value);
    notify('success', '负责人已分配', `已保存 ${result.assigned} 项安排，可在阶段工作中继续调整。`);
    emit('done');
    saving.value = false;
    close();
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <button class="button button-secondary" type="button" @click="show">按团队自动分配</button>
  <AppDrawer v-if="open" :title="`按团队分配负责人 · ${project.projectNo}`" width="min(1050px, 96vw)" @close="close">
    <div class="assignment-body">
    <p class="assignment-lede">先核对建议，再确认保存。已有负责人、手动留空、已完成和不适用的任务不参与本次分配。</p>
    <p v-if="loading" role="status">正在按项目团队匹配…</p>
    <p v-if="error" class="assignment-error" role="alert">{{ error }}</p>
    <template v-if="preview && !loading">
      <p v-if="!preview.rows.length">当前没有需要分配的任务。</p>
      <template v-else>
        <p>待分配 {{ preview.rows.length }} 项 · 唯一匹配 {{ preview.suggested }} 项 · 本次将保存 {{ assignments.length }} 项</p>
        <p class="assignment-help">未匹配或对应多人时，请从项目团队选择；选“暂不分配”可跳过这一项。岗位和角色只是匹配依据，任务归属以你的确认为准。</p>
        <div class="assignment-table">
          <table>
            <thead><tr><th>任务</th><th>参考角色</th><th>建议依据</th><th>确认负责人</th></tr></thead>
            <tbody>
              <tr v-for="row in preview.rows" :key="row.nodeId">
                <td>{{ row.name }}</td><td>{{ row.role || '未设置' }}</td><td>{{ row.reason }}</td>
                <td>
                  <select v-model="choices[row.nodeId]" :disabled="saving" :aria-label="`${row.name} 的确认负责人`">
                    <option :value="null">暂不分配</option>
                    <option v-for="person in preview.people" :key="person.id" :value="person.id">
                      {{ person.name }} · {{ person.roles.join('、') }}{{ person.jobTitle ? `（岗位：${person.jobTitle}）` : '' }}
                    </option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
    <div class="assignment-actions">
      <button class="button button-secondary" type="button" :disabled="saving" @click="close">取消</button>
      <button class="button button-secondary" type="button" :disabled="saving || loading" @click="refresh">重新匹配</button>
      <button class="button button-primary" type="button" :disabled="saving || loading || !assignments.length" @click="confirm">
        {{ saving ? '正在保存…' : `确认分配 ${assignments.length} 项` }}
      </button>
    </div>
    </div>
  </AppDrawer>
</template>

<style scoped>
.assignment-body { padding: 20px; }
.assignment-lede { line-height: 1.7; }
.assignment-help { color: #65758a; font-size: 13px; line-height: 1.6; }
.assignment-error { color: #b42318; }
.assignment-table { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th, td { text-align: left; padding: 12px 10px; border-bottom: 1px solid #e4e9ef; vertical-align: top; }
th { background: #f1f5f9; }
td:nth-child(1) { min-width: 160px; }
td:nth-child(3) { min-width: 160px; color: #65758a; }
select { min-width: 200px; max-width: 360px; width: 100%; padding: 8px; border: 1px solid #cad3df; border-radius: 5px; background: white; }
.assignment-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; margin-top: 20px; }
</style>
